import { gunzipSync, strFromU8 } from 'fflate';

export const ABI_PAYLOAD_ENCODING = 'gzip+base64';

export const ENCODED_ABI_EDI_PREFIX = `${ABI_PAYLOAD_ENCODING}:`;

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

export interface AbiJson {
  [key: string]: JsonValue;
}

export interface AbiExchangeMessage {
  edi: string;
  json: AbiJson;
  key: string;
  time: string;
  ediDecodeFailed?: boolean;
  jsonDecodeFailed?: boolean;
}

interface DecodeResult<T> {
  value: T;
  failed: boolean;
  error?: Error;
}

export interface DecodedAbiExchangeMessages {
  messages: AbiExchangeMessage[];
  errors: Error[];
}

const isEncodedAbiEdi = (edi: string): boolean =>
  typeof edi === 'string' && edi.startsWith(ENCODED_ABI_EDI_PREFIX);

const isEncodedAbiJson = (json: AbiJson): boolean =>
  json?.encoding === ABI_PAYLOAD_ENCODING && typeof json.payload === 'string';

const isAbiJson = (value: JsonValue): value is AbiJson =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const gunzipBase64 = (value: string): string => {
  const binary = atob(value);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return strFromU8(gunzipSync(bytes));
};

const parseAbiJson = (decoded: string): AbiJson => {
  const parsed: JsonValue = JSON.parse(decoded);
  if (!isAbiJson(parsed)) {
    throw new Error('the decompressed JSON is not an object');
  }
  return parsed;
};

const tryDecode = <T>(
  decode: () => T,
  fallback: T,
  { key, time }: AbiExchangeMessage,
): DecodeResult<T> => {
  try {
    return { value: decode(), failed: false };
  } catch (e) {
    const reason = e instanceof Error ? e.message : String(e);
    return {
      value: fallback,
      failed: true,
      error: new Error(
        `Could not decompress the ABI payload of ${key} message at ${time}: ${reason}`,
      ),
    };
  }
};

const notEncoded = <T>(value: T): DecodeResult<T> => ({ value, failed: false });

const decodeAbiExchangeMessage = (
  message: AbiExchangeMessage,
): DecodedAbiExchangeMessages => {
  const { edi, json } = message;
  const ediIsEncoded = isEncodedAbiEdi(edi);
  const jsonIsEncoded = isEncodedAbiJson(json);
  const hasEncodedPayload = ediIsEncoded || jsonIsEncoded;
  if (!hasEncodedPayload) return { messages: [message], errors: [] };
  const decodedEdi = ediIsEncoded
    ? tryDecode(
        () => gunzipBase64(edi.slice(ENCODED_ABI_EDI_PREFIX.length)),
        '',
        message,
      )
    : notEncoded(edi);
  const decodedJson = jsonIsEncoded
    ? tryDecode(
        () => parseAbiJson(gunzipBase64(String(json.payload))),
        {},
        message,
      )
    : notEncoded(json);
  return {
    messages: [
      {
        ...message,
        edi: decodedEdi.value,
        json: decodedJson.value,
        ediDecodeFailed: decodedEdi.failed,
        jsonDecodeFailed: decodedJson.failed,
      },
    ],
    errors: [decodedEdi.error, decodedJson.error].filter(
      (error): error is Error => error !== undefined,
    ),
  };
};

export const decodeAbiExchangeMessages = (
  messages: AbiExchangeMessage[],
): DecodedAbiExchangeMessages =>
  messages.map(decodeAbiExchangeMessage).reduce(
    (decoded, { messages: decodedMessages, errors }) => ({
      messages: [...decoded.messages, ...decodedMessages],
      errors: [...decoded.errors, ...errors],
    }),
    { messages: [], errors: [] } as DecodedAbiExchangeMessages,
  );
